import { z } from 'zod';
import { uuidSchema } from './common.schema.js';
import { PaginationQuerySchema } from './pagination.schema';

/**
 * Patient Schemas - aligned with Prisma models (snake_case)
 */

// ─── Row Schemas (mirror DB columns) ─────────────────────────────────────────

export const BasePatientSchema = z.object({
  patient_id: uuidSchema,
  hospital_id: uuidSchema,
  mrn: z.string().max(50),
  first_name: z.string().min(1).max(100),
  last_name: z.string().min(1).max(100),
  date_of_birth: z.coerce.date(),
  gender: z.string().max(20).nullable().optional(),
  birth_weight_grams: z.number().nullable().optional(),
  gestational_age_weeks: z.number().nullable().optional(),
  multiple_birth_ind: z.boolean().nullable().optional(),
  multiple_birth_ord: z.number().int().nullable().optional(),
  is_active: z.boolean().default(true),
  deceased_ind: z.boolean().default(false),
  deceased_dt: z.coerce.date().nullable().optional(),
  source: z.string().max(10).default('MANUAL'),
  pseudonym_id: uuidSchema.nullable().optional(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
});
export type BasePatient = z.infer<typeof BasePatientSchema>;

export const EncounterRowSchema = z.object({
  encounter_id: uuidSchema,
  patient_id: uuidSchema,
  hospital_id: uuidSchema,
  encounter_number: z.string().nullable().optional(),
  encounter_type: z.string().nullable().optional(),
  patient_type_at_admit: z.string().nullable().optional(),
  admit_date: z.coerce.date(),
  discharge_date: z.coerce.date().nullable().optional(),
  status: z.string(),
  discharge_disposition: z.string().max(30).nullable().optional(),
  attending_staff_id: uuidSchema.nullable().optional(),
  current_bed_id: uuidSchema.nullable().optional(),
  source: z.string().nullable().optional(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
});
export type EncounterRow = z.infer<typeof EncounterRowSchema>;

export const EncounterLocationRowSchema = z.object({
  id: uuidSchema,
  encounter_id: uuidSchema,
  hospital_id: uuidSchema,
  facility_id: uuidSchema.nullable().optional(),
  care_unit_id: uuidSchema.nullable().optional(),
  bed_id: uuidSchema.nullable().optional(),
  started_at: z.coerce.date(),
  ended_at: z.coerce.date().nullable().optional(),
  transfer_reason: z.string().nullable().optional(),
  created_at: z.coerce.date(),
});
export type EncounterLocationRow = z.infer<typeof EncounterLocationRowSchema>;

export const PatientAllergyRowSchema = z.object({
  id: uuidSchema,
  patient_id: uuidSchema,
  hospital_id: uuidSchema,
  allergen_id: uuidSchema,
  severity_id: uuidSchema.nullable().optional(),
  reaction_description: z.string().nullable().optional(),
  onset_date: z.coerce.date().nullable().optional(),
  source: z.string().max(10).default('MANUAL'),
  status: z.string().max(20).default('ACTIVE'),
  verified_by: uuidSchema.nullable().optional(),
  verified_at: z.coerce.date().nullable().optional(),
  notes: z.string().nullable().optional(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
});
export type PatientAllergyRow = z.infer<typeof PatientAllergyRowSchema>;

export const MotherInfantLinkRowSchema = z.object({
  link_id: uuidSchema,
  hospital_id: uuidSchema,
  person_id: uuidSchema,
  patient_id: uuidSchema,
  relationship_type: z.string(),
  effective_from: z.coerce.date(),
  effective_to: z.coerce.date().nullable().optional(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date().optional(),
});
export type MotherInfantLinkRow = z.infer<typeof MotherInfantLinkRowSchema>;

// ─── Request Schemas ──────────────────────────────────────────────────────────

export const CreatePatientRequestSchema = z.object({
  hospital_id: uuidSchema,
  mrn: z.string().min(1).max(50),
  first_name: z.string().min(1, 'First name is required').max(100),
  middle_name: z.string().max(100).optional(),
  last_name: z.string().min(1, 'Last name is required').max(100),
  date_of_birth: z.string().datetime({ offset: true }).describe('Date and time of birth (ISO 8601 with offset, e.g. 2024-05-12T14:30:00+03:00)'),
  gender: z.string().max(20),
  birth_weight_grams: z.number().nonnegative().nullable(),
  multiple_birth_ind: z.boolean().nullable().optional(),
  multiple_birth_ord: z.number().positive().nullable().optional().describe('Birth order for multiples (e.g. 1, 2, 3)'),
  source: z.enum(['ADT', 'MANUAL']).optional().describe('Data source — defaults to MANUAL'),
  // ── Admission info ────────────────────────────────────────────────────────
  facility_id: uuidSchema,
  unit_id: uuidSchema,
  bed_id: uuidSchema.optional(),
  staff_id: uuidSchema,
  // ── Optional allergies ────────────────────────────────────────────────────
  allergies: z.array(z.object({
    allergen_id: uuidSchema,
    severity_id: uuidSchema,
  })).optional().describe('Known patient allergies (omit to record none)'),
  // ── Optional mother link ──────────────────────────────────────────────────
  person_id: uuidSchema.nullable().optional().describe('UUID of the mother patient record (neonates)'),
  // ── Donor consent ─────────────────────────────────────────────────────────
  donor_consent: z.boolean().optional().describe('True if the patient has granted donor milk consent'),  // ── Encounter (optional sub-object) ───────────────────────────────────────────
  encounter: z.object({
    encounter_number:      z.string().optional(),
    patient_type_at_admit: z.string().optional(),
    source:                z.string().optional(),
  }).optional().describe('Encounter data for the initial admission'),});
export type CreatePatientRequest = z.infer<typeof CreatePatientRequestSchema>;

export const UpdatePatientRequestSchema = z.object({
  // ── Patient demographics ─────────────────────────────────────────────────
  hospital_id: uuidSchema,
  mrn: z.string().min(1).max(50).optional().describe('Patient MRN'),
  first_name: z.string().min(1).max(100).optional().describe('Patient first name'),
  middle_name: z.string().max(100).nullable().optional().describe('Patient middle name'),
  last_name: z.string().min(1).max(100).optional().describe('Patient last name'),
  date_of_birth: z.string().datetime({ offset: true }).optional().describe('Date and time of birth (ISO 8601 with offset, e.g. 2024-05-12T14:30:00+03:00)'),
  gender: z.string().max(20).nullable().optional().describe('Patient gender'),
  birth_weight_grams: z.number().nonnegative().nullable().optional().describe('Birth weight in grams'),
  multiple_birth_ind: z.boolean().nullable().optional().describe('True if part of a multiple birth'),
  multiple_birth_ord: z.number().positive().nullable().optional().describe('Birth order for multiples (e.g. 1, 2, 3)'),
  source: z.enum(['ADT', 'MANUAL']).optional().describe('Data source — defaults to MANUAL'),
  gestational_age_weeks: z.number().nullable().optional().describe('Gestational age in weeks'),
  is_active: z.boolean().optional().describe('Whether the patient record is active'),
  deceased_ind: z.boolean().optional().describe('Deceased indicator'),
  deceased_dt: z.string().datetime({ offset: true }).nullable().optional().describe('Date and time of death'),
  // ── Encounter ────────────────────────────────────────────────────────────
  encounter: z.object({
    encounter_id: uuidSchema,
    encounter_status: z.enum(['ACTIVE', 'DISCHARGED', 'TRANSFERRED', 'CLOSED']).optional().describe('Encounter status'),
  }).optional().describe('Patient encounter data'),
  encounter_location: z.object({
    encounter_facility_id: z.union([uuidSchema, z.literal('')]).describe('Encounter facility UUID (may be empty when unknown)'),
    facility_id: uuidSchema.optional().describe('New location UUID - triggers a location transfer'),
    unit_id: uuidSchema.optional().describe('New care unit UUID — triggers a location transfer'),
    bed_id: uuidSchema.nullable().optional().describe('New bed UUID — triggers a location transfer'),
    staff_id: uuidSchema.optional().describe('UUID of the staff member recording this update'),
    transfer_reason: z.string().nullable().optional().describe('Reason for location transfer'),
  }).optional().describe('Patient encounter location data'),
  // ── Allergies (full replace — omit to leave unchanged) ───────────────────
  allergies: z.array(z.object({
    allergen_id: uuidSchema,
    severity_id: uuidSchema,
    reaction_description: z.string().nullable().optional().describe('Free-text description of the reaction'),
    source: z.enum(['ADT', 'MANUAL']).optional().describe('Data source — defaults to MANUAL'),
  })).optional().describe('Full replacement allergy list — omit to leave existing allergies unchanged'),
  // ── Mother link ───────────────────────────────────────────────────────────
  link_id: uuidSchema.optional(),
  person_id: uuidSchema.nullable().optional().describe('UUID of the mother patient record (neonates)'),
  // ── Donor consent ─────────────────────────────────────────────────────────
  donor_consent: z.boolean().optional().describe('True to grant donor milk consent, false to withdraw'),
});
export type UpdatePatientRequest = z.infer<typeof UpdatePatientRequestSchema>;

// ─── Response Schemas ─────────────────────────────────────────────────────────

export const PatientResponseSchema = BasePatientSchema;
export type PatientResponse = z.infer<typeof PatientResponseSchema>;

export const SubjectPseudonymRowSchema = z.object({
  pseudonym_id: uuidSchema,
  subject_type: z.string().max(10),
  subject_id: uuidSchema,
  hospital_id: uuidSchema,
  pseudonym: z.string(),
  pseudonym_scope: z.string().max(20).nullable().optional(),
  created_at: z.coerce.date(),
});
export type SubjectPseudonymRow = z.infer<typeof SubjectPseudonymRowSchema>;

export const PatientMutationResponseSchema = z.object({
  // ── patient fields ──────────────────────────────────────────────────────
  patient_id: uuidSchema,
  hospital_id: uuidSchema,
  mrn: z.string(),
  first_name: z.string(),
  middle_name: z.string().nullable().optional(),
  last_name: z.string(),
  date_of_birth: z.coerce.date(),
  gender: z.string().nullable().optional(),
  birth_weight_grams: z.number().nullable().optional(),
  gestational_age_weeks: z.number().nullable().optional(),
  multiple_birth_ind: z.boolean().nullable().optional(),
  multiple_birth_ord: z.number().int().nullable().optional(),
  is_active: z.boolean(),
  deceased_ind: z.boolean(),
  deceased_dt: z.coerce.date().nullable().optional(),
  source: z.string(),
  pseudonym_id: uuidSchema.nullable().optional(),
  staff_id: uuidSchema.nullable().optional(),

  // ── encounter fields ────────────────────────────────────────────────────
  encounter_id: uuidSchema,
  encounter_status: z.string(),

  // ── encounter_location fields ───────────────────────────────────────────
  encounter_location_id: uuidSchema,
  facility_id: uuidSchema.nullable().optional(),
  care_unit_id: uuidSchema.nullable().optional(),
  bed_id: uuidSchema.nullable().optional(),
  encounter_location_started_at: z.coerce.date(),

  // ── allergies ───────────────────────────────────────────────────────────
  allergies: z.array(z.object({
    id: uuidSchema,
    allergen_id: uuidSchema,
    severity_id: uuidSchema.nullable().optional(),
  })),

  // ── mother_infant_link ──────────────────────────────────────────────────
  mother_infant_link: z.object({
    link_id: uuidSchema,
    person_id: uuidSchema,
    patient_id: uuidSchema,
    relationship_type: z.string(),
  }).nullable(),

  // ── consent record (only present when donor_consent was true) ───────────
  donor_consent: z.boolean().optional(),
});
export type PatientMutationResponse = z.infer<typeof PatientMutationResponseSchema>;

// Aliases — both create and update share the same response shape
export const CreatePatientResponseSchema = PatientMutationResponseSchema;
export type CreatePatientResponse = PatientMutationResponse;

export const UpdatePatientResponseSchema = PatientMutationResponseSchema;
export type UpdatePatientResponse = PatientMutationResponse;

// ─── GET single-patient response ──────────────────────────────────────────────

export const GetPatientByIdResponseSchema = z.object({
  // ── patient fields ──────────────────────────────────────────────────────
  patient_id: uuidSchema,
  hospital_id: uuidSchema,
  mrn: z.string(),
  first_name: z.string(),
  middle_name: z.string().nullable().optional(),
  last_name: z.string(),
  date_of_birth: z.coerce.date(),
  gender: z.string().nullable().optional(),
  birth_weight_grams: z.number().nullable().optional(),
  gestational_age_weeks: z.number().nullable().optional(),
  multiple_birth_ind: z.boolean().nullable().optional(),
  multiple_birth_ord: z.number().int().nullable().optional(),
  is_active: z.boolean(),
  deceased_ind: z.boolean(),
  deceased_dt: z.coerce.date().nullable().optional(),
  source: z.string(),
  pseudonym_id: uuidSchema.nullable().optional(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),

  // ── active encounter ────────────────────────────────────────────────────
  encounter: z.object({
    encounter_id: uuidSchema,
    encounter_number: z.string().nullable().optional(),
    encounter_type: z.string().nullable().optional(),
    status: z.string(),
    admit_date: z.coerce.date(),
    discharge_date: z.coerce.date().nullable().optional(),
    attending_staff_id: uuidSchema.nullable().optional(),
  }).nullable(),

  // ── current encounter location ──────────────────────────────────────────
  encounter_location: z.object({
    id: uuidSchema,
    facility_id: uuidSchema.nullable().optional(),
    facility_code: z.string().nullable().optional(),
    care_unit_id: uuidSchema.nullable().optional(),
    unit_code: z.string().nullable().optional(),
    bed_id: uuidSchema.nullable().optional(),
    bed_number: z.string().nullable().optional(),
    started_at: z.coerce.date(),
  }).nullable(),

  // ── allergies ───────────────────────────────────────────────────────────
  allergies: z.array(z.object({
    id: uuidSchema,
    allergen_id: uuidSchema,
    severity_id: uuidSchema.nullable().optional(),
    reaction_description: z.string().nullable().optional(),
    status: z.string(),
  })),

  // ── mother_infant_link ──────────────────────────────────────────────────
  mother_infant_link: z.object({
    link_id: uuidSchema,
    person_id: uuidSchema,
    patient_id: uuidSchema,
    relationship_type: z.string(),
    mother_first_name: z.string().nullable().optional(),
    mother_last_name: z.string().nullable().optional(),
  }).nullable(),

  // ── donor consent ───────────────────────────────────────────────────────
  donor_consent: z.boolean(),

  // ── active patient assignments (effective_to is null) ───────────────────
  patient_assignments: z.array(z.object({
    assignment_id: uuidSchema,
    encounter_id: uuidSchema,
    staff_id: z.string().min(1).max(200),
    staff_first_name: z.string(),
    staff_last_name: z.string(),
    staff_phone_number: z.string().nullable().optional(),
    hospital_id: uuidSchema,
    assignment_role: z.string(),
    effective_from: z.coerce.date(),
    notes: z.string().nullable().optional(),
    created_by: z.string().max(200).nullable().optional(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
  })),
});
export type GetPatientByIdResponse = z.infer<typeof GetPatientByIdResponseSchema>;

// ─── Patient list query ───────────────────────────────────────────────────────

export const PatientListQuerySchema = PaginationQuerySchema.extend({
  search: z.string().optional().describe('Search by name or MRN'),
  staff_id: uuidSchema.optional().describe('Filter by assigned staff member (active assignments only)'),
  hospital_id: uuidSchema.optional().describe('Filter by hospital'),
  gender: z.string().optional().describe('Filter by gender (e.g. MALE, FEMALE)'),
  admit_date_from: z.coerce.date().optional().describe('Filter encounters admitted on or after this date'),
  admit_date_to: z.coerce.date().optional().describe('Filter encounters admitted on or before this date'),
  from: z.coerce.date().optional().describe('Filter patients born on or after this date (ISO 8601 timestamp, timezone-aware)'),
  to: z.coerce.date().optional().describe('Filter patients born on or before this date (ISO 8601 timestamp, timezone-aware)'),
  facility_id: uuidSchema.optional().describe('Filter by current facility'),
  care_unit_id: uuidSchema.optional().describe('Filter by current care unit'),
  donor_consent: z.enum(['true', 'false']).optional().describe('Filter by donor consent status'),
  is_active: z.enum(['true', 'false', 'all']).optional().describe('Filter by patient active status. true = active/admitted (default), false = discharged/inactive, all = both'),
});
export type PatientListQuery = z.infer<typeof PatientListQuerySchema>;

// ─── GET patient list response ────────────────────────────────────────────────

export const PatientListItemSchema = z.object({
  // ── patient fields ──────────────────────────────────────────────────────
  patient_id: uuidSchema,
  hospital_id: uuidSchema,
  mrn: z.string(),
  first_name: z.string(),
  middle_name: z.string().nullable().optional(),
  last_name: z.string(),
  date_of_birth: z.coerce.date(),
  gender: z.string().nullable().optional(),
  birth_weight_grams: z.number().nullable().optional(),
  gestational_age_weeks: z.number().nullable().optional(),
  is_active: z.boolean(),

  // ── active encounter ────────────────────────────────────────────────────
  encounter: z.object({
    encounter_id: uuidSchema,
    encounter_number: z.string().nullable().optional(),
    encounter_type: z.string().nullable().optional(),
    status: z.string(),
    admit_date: z.coerce.date(),
    discharge_date: z.coerce.date().nullable().optional(),
  }).nullable(),

  // ── current encounter location ──────────────────────────────────────────
  encounter_location: z.object({
    id: uuidSchema,
    facility_id: uuidSchema.nullable().optional(),
    facility_code: z.string().nullable().optional(),
    care_unit_id: uuidSchema.nullable().optional(),
    unit_code: z.string().nullable().optional(),
    bed_id: uuidSchema.nullable().optional(),
    bed_number: z.string().nullable().optional(),
    started_at: z.coerce.date(),
  }).nullable(),

  // ── donor consent status ────────────────────────────────────────────────
  donor_consent: z.boolean(),
  // ── linked mother (from active mother_infant_links) ──────────────────────
  mother: z.object({
    person_id:   uuidSchema,
    person_type: z.string().describe('MOTHER | BOTH'),
  }).nullable().optional()
    .describe('Linked milk provider (mother) for this patient via an active mother_infant_links row'),});
export type PatientListItem = z.infer<typeof PatientListItemSchema>;

export const GetPatientListResponseSchema = z.object({
  items: z.array(PatientListItemSchema),
  pagination: z.object({
    currentPage: z.number(),
    totalPages: z.number(),
    totalItems: z.number(),
    itemsPerPage: z.number(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
  }),
});
export type GetPatientListResponse = z.infer<typeof GetPatientListResponseSchema>;

// ─── Discharge patient ────────────────────────────────────────────────────────

export const DischargePatientRequestSchema = z.object({
  discharge_date: z.string().datetime({ offset: true, local: true }).optional()
    .describe('Discharge date/time (ISO 8601 with offset). Defaults to current time if omitted'),
  discharge_disposition: z.string().max(30).optional().nullable()
    .describe('Discharge disposition code (e.g. HOME, TRANSFERRED, EXPIRED)'),
  discharge_reason: z.string().max(500).optional().nullable()
    .describe('Free-text clinical reason for discharge'),
  mother_discharge_role: z.enum(['MOTHER', 'DONOR', 'BOTH']).optional()
    .describe('Role to discharge for the linked mother (set by UI after user confirmation popup). When provided the last-active-child check is skipped. Omit to use automatic last-active-child detection.'),
  discharge_mother: z.boolean().default(false)
    .describe('When true, also discharge the linked mother (milk provider) via the existing automatic workflow. Default false — patient-only discharge.'),
  disable_user: z.boolean().default(false)
    .describe('When true, also disables the linked mother\'s Cognito donor account if she has a DONOR role. Passed through to the milk-provider discharge call. Silently ignored when the mother has no DONOR role.'),
});
export type DischargePatientRequest = z.infer<typeof DischargePatientRequestSchema>;

export const DischargePatientResponseSchema = z.object({
  patient_id: uuidSchema,
  hospital_id: uuidSchema,
  is_active: z.literal(false),
  encounter_id: uuidSchema,
  encounter_status: z.literal('DISCHARGED'),
  discharge_date: z.string().datetime(),
  discharge_disposition: z.string().nullable().optional(),
  discharged_at: z.string().datetime(),
  related_records_closed: z.object({
    bed_freed: z.boolean(),
    patient_assignments: z.number().int(),
    feed_orders: z.number().int(),
    feed_schedules: z.number().int(),
    milk_allocations: z.number().int(),
    current_medications: z.number().int(),
  }),
  prepared_feeds_sent_home: z.array(z.object({
    split_id:              uuidSchema,
    prep_id:               uuidSchema,
    split_sequence:        z.number().int(),
    volume_ml:             z.number(),
    syringe_label_barcode: z.string().nullable().optional(),
    label_expiry_at:       z.string().nullable().optional(),
  })).optional()
    .describe('Feed prep splits that were PREPARED at discharge — automatically updated to SEND_HOME'),
  mother_auto_discharge: z.object({
    triggered:         z.boolean().describe('true when at least one mother was auto-discharged'),
    persons_discharged: z.array(uuidSchema)
      .describe('person_ids of mothers automatically discharged because this was their last active infant'),
  }).optional().describe('Present when the patient had a linked mother; absent otherwise'),
});
export type DischargePatientResponse = z.infer<typeof DischargePatientResponseSchema>;

// ─── Discharge patient — pre-submit preview ───────────────────────────────────

export const DischargePreviewResponseSchema = z.object({
  patient_id: uuidSchema,
  prepared_feeds: z.array(z.object({
    split_id:              uuidSchema,
    prep_id:               uuidSchema,
    split_sequence:        z.number().int(),
    volume_ml:             z.number(),
    syringe_label_barcode: z.string().nullable().optional(),
    label_expiry_at:       z.string().nullable().optional(),
  })).describe('Feed prep splits currently PREPARED for this patient — will be set to SEND_HOME if the patient is discharged now'),
});
export type DischargePreviewResponse = z.infer<typeof DischargePreviewResponseSchema>;

// ─── Re-admit patient ────────────────────────────────────────────────────────────────

export const ReadmitPatientRequestSchema = z.object({
  facility_id: uuidSchema.optional()
    .describe('Facility to re-admit into (strongly recommended)'),
  unit_id: uuidSchema.optional()
    .describe('Care unit (optional if not yet assigned)'),
  bed_id: uuidSchema.optional().nullable()
    .describe('Specific bed assignment'),
  staff_id: uuidSchema.optional()
    .describe('Attending staff UUID — creates a new patient assignment'),
  admit_date: z.string().datetime({ offset: true, local: true }).optional()
    .describe('Admit date/time (ISO 8601 with offset). Defaults to current time if omitted'),
  encounter_number: z.string().max(100).optional().nullable()
    .describe('Hospital encounter number'),
  patient_type_at_admit: z.string().max(20).optional().nullable()
    .describe('Patient type at admit (e.g. INPATIENT)'),
  readmit_mother: z.boolean().optional()
    .describe('When true and the linked mother/milk-provider record is currently discharged, also re-admit her MOTHER role (calls MilkProviderService.reAdmitMother internally). Soft-fail — never blocks the patient re-admission itself.'),
});
export type ReadmitPatientRequest = z.infer<typeof ReadmitPatientRequestSchema>;

export const ReadmitPatientResponseSchema = z.object({
  patient_id: uuidSchema,
  hospital_id: uuidSchema,
  is_active: z.literal(true),
  encounter_id: uuidSchema,
  encounter_status: z.literal('ACTIVE'),
  admit_date: z.string().datetime(),
  facility_id: uuidSchema.nullable().optional(),
  unit_id: uuidSchema.nullable().optional(),
  bed_id: uuidSchema.nullable().optional(),
  readmitted_at: z.string().datetime(),
  mother_auto_readmit: z.object({
    triggered: z.boolean().describe('true when the linked mother\'s MOTHER role was successfully re-admitted'),
    person_id: uuidSchema,
  }).optional().describe('Present only when the patient had a linked, currently-discharged mother AND readmit_mother=true was requested'),
});
export type ReadmitPatientResponse = z.infer<typeof ReadmitPatientResponseSchema>;
